import React, { ReactElement } from 'react';
import { useCommon } from 'src/AppState/Common';
import { RouterBackoffice } from 'src/AppState/RouterBackoffice';
import { CampaignCreatorState } from 'src/domains/casino/webview/bonus-engine/campaign/CampaignCreator.state';
import { BadgeStatus, BadgeStatusVariantType } from 'src/domains/common/components/badge/badge';
import { Button } from 'src/domains/common/components/button/button';
import { observer } from 'src/utils/mobx-react';
import { CampaignViewManager } from './CampaignViewManager';
import { CampaignStatusType } from 'src/api_openapi/v2/bonus-engine/schemas';
import {
    NewCampaignButtons,
    DraftCampaignButtons,
    ActiveCampaignButtons,
    PausedCampaignButtons,
} from './CampaignHeaderButtons';

function formTitle(routerBackoffice: RouterBackoffice, campaignStatus?: CampaignStatusType): string {
    if (typeof routerBackoffice.bonusCampaignCloneIdParam === 'string') {
        return 'Duplicated Campaign';
    } else if (typeof routerBackoffice.bonusCampaignIdParam === 'string' && campaignStatus !== undefined) {
        return `${campaignStatus.charAt(0).toUpperCase() + campaignStatus.slice(1)} Campaign`;
    }
    return 'New Campaign';
}

function formBadgeVariant(
    routerBackoffice: RouterBackoffice,
    campaignStatus?: CampaignStatusType
): BadgeStatusVariantType {
    if (typeof routerBackoffice.bonusCampaignIdParam === 'string') {
        if (campaignStatus === 'expired') {
            return 'readonly';
        } else {
            return 'edit';
        }
    }
    return 'unsaved';
}

interface CampaignHeaderPropsType {
    routerBackoffice: RouterBackoffice;
    campaignId: string | undefined;
    campaignState: CampaignViewManager;
}

export const CampaignHeader = observer(
    'CampaignHeader',
    ({ routerBackoffice, campaignId, campaignState }: CampaignHeaderPropsType): ReactElement | null => {
        const common = useCommon();
        const createCampaignState = CampaignCreatorState.get(common, campaignId);
        const { handleCloseWithDiscard, handleClose } = campaignState;
        const campaignStatus = createCampaignState.campaign?.status;

        return (
            <header className='flex items-center justify-between max-w-full'>
                <div className='min-w-0'>
                    <h1
                        className='truncate m-0 text-[1.5rem] font-semibold leading-[1.25] mb-2'
                        data-test='campaign-name'
                    >
                        {createCampaignState.generalDetailsState.campaignDisplayName}
                    </h1>
                    <div className='flex items-center gap-3'>
                        <p
                            className='m-0'
                            data-test='campaign-form-title'
                        >
                            {formTitle(routerBackoffice, campaignStatus)}
                        </p>
                        <BadgeStatus
                            variant={formBadgeVariant(routerBackoffice, campaignStatus)}
                            dataTest='campaign-status'
                        />
                    </div>
                </div>
                <div className='flex flex-none gap-2'>
                    {routerBackoffice.bonusCampaignParams?.type === 'NEW' ? (
                        <NewCampaignButtons
                            onClose={handleCloseWithDiscard}
                            onSaveAsDraft={() => createCampaignState.createCampaign('draft')}
                            onActivate={() => createCampaignState.createCampaign('active')}
                        />
                    ) : (
                        <>
                            {campaignStatus === 'draft' && (
                                <DraftCampaignButtons
                                    onClose={handleCloseWithDiscard}
                                    onUpdate={createCampaignState.updateCampaign}
                                    onActivate={createCampaignState.activateCampaign}
                                />
                            )}
                            {(campaignStatus === 'active' || campaignStatus === 'ready') && (
                                <ActiveCampaignButtons
                                    onClose={handleCloseWithDiscard}
                                    onPause={() => createCampaignState.updateCampaignStatus('paused')}
                                    onUpdate={createCampaignState.updateCampaignEndDate}
                                />
                            )}
                            {campaignStatus === 'paused' && (
                                <PausedCampaignButtons
                                    onClose={handleClose}
                                    onUpdate={createCampaignState.updateCampaignEndDate}
                                    onReactivate={() => createCampaignState.updateCampaignStatus('active')}
                                />
                            )}
                            {campaignStatus === 'expired' && (
                                <Button
                                    variant='ghost'
                                    onClick={handleClose}
                                >
                                    Close
                                </Button>
                            )}
                        </>
                    )}
                </div>
            </header>
        );
    }
);
